import { map } from 'rxjs/operators';
import {
  CoreMenu,
  CoreMobiResource,
  CorePageModuleIDs
} from '../../core.models';
import { MenuIconTypes } from './components/dynamic-menu-icon/dynamic-menu-icon.component';

export interface ImprovedCoreMenuButton {
  action?: 'LOGOUT';
  module?: CorePageModuleIDs;
  resource?: CoreMobiResource;
  pageIndex?: number;
}

export interface ImprovedCoreMenu {
  color: string;
  instanceLogo: string;
  mainButtons: Array<{
    id:
      | 'home'
      | 'messages'
      | 'measures'
      | 'myMeasures'
      | 'events'
      | 'myFiles'
      | 'eDiary';
    icon: MenuIconTypes;
    reversed: boolean;
    iconColor: string;
    notifications?: number;
    flexBasisStyle: string;
    labelTranslateKey: string;
  }> &
    ImprovedCoreMenuButton;
  secondaryButtonGroups: Array<{
    title?: string;
    buttons: Array<{
      icon?: MenuIconTypes;
      label?: string;
      labelTranslateKey?: string;
    }> &
      ImprovedCoreMenuButton;
  }>;
}

export const improveCoreMenu = () =>
  map<CoreMenu, ImprovedCoreMenu>(
    coreMenu =>
      (coreMenu && {
        ...coreMenu,
        instanceLogo:
          coreMenu && coreMenu.instanceLogo.includes('http')
            ? coreMenu.instanceLogo
            : `data:image/svg+xml;base64,${coreMenu.instanceLogo}`,
        mainButtons: [...coreMenu.mainButtons]
          .sort((a, b) => {
            const order = [
              'home',
              'messages',
              'measures',
              'myMeasures',
              'events',
              'myFiles',
              'eDiary'
            ];
            return (
              order.findIndex(id => id === a.id) -
              order.findIndex(id => id === b.id)
            );
          })
          .map(button => {
            const isPresent: (id: string) => number = (id: string) =>
              coreMenu.mainButtons.findIndex(b => b.id === id) > -1 ? 1 : 0;

            const countInFirstLine =
              isPresent('home') + isPresent('messages') + isPresent('measures');

            const firstLineFlexBasis =
              countInFirstLine > 1
                ? `calc(${Math.floor(100 / countInFirstLine)}% - 10px)`
                : '100%';

            switch (button.id) {
              case 'home':
                return {
                  ...button,
                  icon: MenuIconTypes.HOME,
                  module: CorePageModuleIDs.TIMELINE,
                  reversed: countInFirstLine > 1,
                  flexBasisStyle: firstLineFlexBasis,
                  labelTranslateKey: 'MENU_MAIN_BUTTONS.HOME'
                };

              case 'messages':
                return {
                  ...button,
                  icon: MenuIconTypes.MESSAGES,
                  module: CorePageModuleIDs.MESSAGES,
                  reversed: countInFirstLine > 1,
                  flexBasisStyle: firstLineFlexBasis,
                  labelTranslateKey: 'MENU_MAIN_BUTTONS.MESSAGES'
                };

              case 'measures':
                return {
                  ...button,
                  icon: MenuIconTypes.MEASURES,
                  module: CorePageModuleIDs.TAKE_MEASURE,
                  reversed: countInFirstLine > 1,
                  flexBasisStyle: firstLineFlexBasis,
                  labelTranslateKey: 'MENU_MAIN_BUTTONS.MEASURES'
                };

              case 'myMeasures':
                return {
                  ...button,
                  icon: MenuIconTypes.MY_MEASURES,
                  module: CorePageModuleIDs.MY_MEASURES,
                  reversed: false,
                  flexBasisStyle: '100%',
                  labelTranslateKey: 'MENU_MAIN_BUTTONS.MY_MEASURES'
                };

              case 'events':
                return {
                  ...button,
                  icon: MenuIconTypes.EVENTS,
                  module: CorePageModuleIDs.EVENTS,
                  reversed: false,
                  flexBasisStyle: '100%',
                  labelTranslateKey: 'MENU_MAIN_BUTTONS.EVENTS'
                };

              case 'myFiles':
                return {
                  ...button,
                  icon: MenuIconTypes.EDOCUMENTS,
                  module: CorePageModuleIDs.EDOCUMENTS,
                  reversed: countInFirstLine > 1,
                  flexBasisStyle: '100%',
                  labelTranslateKey: 'MENU_MAIN_BUTTONS.EDOCUMENTS'
                };

              case 'eDiary':
                return {
                  ...button,
                  icon: MenuIconTypes.EDIARY,
                  module: CorePageModuleIDs.EDIARY,
                  reversed: countInFirstLine > 1,
                  flexBasisStyle: '100%',
                  labelTranslateKey: 'MENU_MAIN_BUTTONS.EDIARY'
                };

              default:
                console.error('Unknown main button');
                return void 0;
            }
          }),
        secondaryButtonGroups: [
          ...coreMenu.secondaryButtonGroups,
          {
            buttons: [
              {
                icon: MenuIconTypes.HELP,
                module: CorePageModuleIDs.HELP,
                labelTranslateKey: 'HELP'
              },
              {
                icon: MenuIconTypes.PROFILE,
                module: CorePageModuleIDs.MY_ACCOUNT,
                labelTranslateKey: 'ACCOUNT'
              }
            ]
          },
          {
            buttons: [
              {
                icon: MenuIconTypes.LOGOUT,
                action: 'LOGOUT',
                labelTranslateKey: 'LOGOUT'
              }
            ]
          }
        ]
      }) ||
      void 0
  );
